import { useEffect, useRef } from 'react';

/** Constant scroll speed in pixels per requestAnimationFrame callback (~60 fps = ~480 px/s). */
const SCROLL_SPEED_PX = 8;

interface UseArrowAutoScrollOptions {
  scrollContainerRef: React.RefObject<HTMLElement>;
  isDragging: boolean;
  /** Override the default constant scroll speed (px per rAF frame). */
  scrollSpeedPx?: number;
}

interface UseArrowAutoScrollReturn {
  leftArrowRef: React.RefObject<HTMLButtonElement>;
  rightArrowRef: React.RefObject<HTMLButtonElement>;
}

/**
 * Continuously scrolls a carousel container while an active drag is hovering over
 * the left or right arrow buttons.
 *
 * Why window pointermove instead of pointerenter/leave on the buttons:
 *   During an active dnd-kit drag the pointer is captured by the dragged element,
 *   so pointerenter/leave events do not fire on the arrow buttons. We listen on
 *   window and manually hit-test pointer coordinates against each button's
 *   getBoundingClientRect() instead.
 *
 * Scroll speed is constant (no acceleration) per the clarified requirement.
 */
export function useArrowAutoScroll({
  scrollContainerRef,
  isDragging,
  scrollSpeedPx = SCROLL_SPEED_PX,
}: UseArrowAutoScrollOptions): UseArrowAutoScrollReturn {
  const leftArrowRef = useRef<HTMLButtonElement>(null);
  const rightArrowRef = useRef<HTMLButtonElement>(null);

  // Mutable refs so the rAF loop always reads the latest values without re-creating the effect.
  const directionRef = useRef<'left' | 'right' | null>(null);
  const rafIdRef = useRef<number>(0);

  useEffect(() => {
    if (!isDragging) {
      // Stop any active scroll when drag ends.
      directionRef.current = null;
      cancelAnimationFrame(rafIdRef.current);
      return;
    }

    const scroll = () => {
      const container = scrollContainerRef.current;
      if (!container || !directionRef.current) return;

      const speed = directionRef.current === 'right' ? scrollSpeedPx : -scrollSpeedPx;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;

      // Clamp: don't scroll beyond boundaries.
      container.scrollLeft = Math.max(0, Math.min(container.scrollLeft + speed, maxScrollLeft));

      rafIdRef.current = requestAnimationFrame(scroll);
    };

    const onPointerMove = (e: PointerEvent) => {
      const isOverRect = (rect: DOMRect | undefined): boolean => {
        if (!rect) return false;
        return (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom
        );
      };

      const leftRect = leftArrowRef.current?.getBoundingClientRect();
      const rightRect = rightArrowRef.current?.getBoundingClientRect();

      const next: 'left' | 'right' | null = isOverRect(leftRect)
        ? 'left'
        : isOverRect(rightRect)
        ? 'right'
        : null;

      if (next !== directionRef.current) {
        directionRef.current = next;
        cancelAnimationFrame(rafIdRef.current);
        if (next !== null) {
          rafIdRef.current = requestAnimationFrame(scroll);
        }
      }
    };

    window.addEventListener('pointermove', onPointerMove);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      cancelAnimationFrame(rafIdRef.current);
      directionRef.current = null;
    };
  }, [isDragging, scrollContainerRef, scrollSpeedPx]);

  return { leftArrowRef, rightArrowRef };
}
